import { useState, useEffect } from "react";
import { backend } from "@/services/backend";
import { OwnerState, OwnerStats } from "@/types/owner-edit";
import {
  DEFAULT_OWNER_STATE,
  OWNER_EDIT_CONSTANTS,
} from "@/constants/owner-edit";
import { infoDeafaultData } from "@/data/info";
import { ItemFileData } from "@/types/collection";

const fetchMetadata = async (url: string): Promise<ItemFileData | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch metadata: ${response.status}`);
    }
    const metadata = await response.json();
    return metadata;
  } catch (error) {
    console.error("Error fetching metadata:", error);
    return null;
  }
};

export const useOwnerData = (
  address: string | undefined,
  ownerDatas: any,
  reloadTrigger: number = 0
) => {
  const [ownerData, setOwnerData] = useState<OwnerState>(DEFAULT_OWNER_STATE);
  const [originalOwnerData, setOriginalOwnerData] =
    useState<OwnerState>(DEFAULT_OWNER_STATE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Calculate stats from owner data
  const stats: OwnerStats = {
    created_collections: ownerDatas?.collections?.length || 0,
    minted_nfts: ownerDatas?.items?.length || 0,
    earned: Number(
      (
        ownerDatas?.collections?.reduce(
          (sum: number, collection: any) =>
            sum + (collection.next_item_index * collection.price) / 1000000000,
          0
        ) || 0
      ).toFixed(2)
    ),
  };

  // Initialize username from address if not set
  useEffect(() => {
    if (!address || ownerData.username) return;
    const prefix = address.slice(0, 4);
    const suffix = address.slice(-4);
    setOwnerData(prev => ({
      ...prev,
      username: `USER#${prefix}...${suffix}`,
    }));
  }, [address, ownerData.username]);

  // Fetch owner data
  useEffect(() => {
    const fetchOwnerData = async () => {
      if (!address) {
        setIsLoading(false);
        return;
      }

      try {
        if (reloadTrigger === 0) setIsLoading(true);
        setError(null);
        const [extendInfo, photos, description, socials, username] =
          await Promise.all([
            backend.getUserExtendedProfile(address),
            backend.getOwnerPhotos(address),
            backend.getWalletDescription(address),
            backend.getUserSocials(address),
            backend.getUserDisplayName(address),
          ]);

        console.log("extendInfo", extendInfo, photos);

        // Fetch metadata from extendInfo if available
        let avatarFromMetadata = "";
        let backgroundFromMetadata = "";

        if (extendInfo?.avatar_data_url) {
          const avatarMetadata = await fetchMetadata(
            extendInfo.avatar_data_url
          );
          if (avatarMetadata?.image) {
            avatarFromMetadata = avatarMetadata.image;
          }
        }

        if (extendInfo?.background_data_url) {
          const backgroundMetadata = await fetchMetadata(
            extendInfo.background_data_url
          );
          if (backgroundMetadata?.image) {
            backgroundFromMetadata = backgroundMetadata.image;
          }
        }

        const avatarPhotos = photos.filter((p: any) => p.category === "avatar");
        const latestAvatar =
          avatarPhotos.length > 0
            ? avatarPhotos.reduce((prev: any, curr: any) =>
                prev.id > curr.id ? prev : curr
              )
            : { id: -1, url: "" };

        const backgroundPhotos = photos.filter(
          (p: any) => p.category === "cover"
        );
        const latestBackground =
          backgroundPhotos.length > 0
            ? backgroundPhotos.reduce((prev: any, curr: any) =>
                prev.id > curr.id ? prev : curr
              )
            : { id: -1, url: "" };

        const socialLinksMap = socials.reduce((acc: any, social: any) => {
          acc[social.name] = social.link;
          return acc;
        }, {});

        const updatedSocials = DEFAULT_OWNER_STATE.socials.map(social => ({
          ...social,
          link: socialLinksMap[social.name] || "",
        }));

        const initialState: OwnerState = {
          extendInfo: extendInfo,
          username: username || "",
          avatar:
            avatarFromMetadata ||
            latestAvatar.url ||
            OWNER_EDIT_CONSTANTS.DEFAULT_AVATAR,
          avatarId: latestAvatar.id,
          background:
            backgroundFromMetadata ||
            latestBackground.url ||
            OWNER_EDIT_CONSTANTS.DEFAULT_BACKGROUND,
          backgroundId: latestBackground.id,
          info:
            description ||
            infoDeafaultData[
              Math.floor(Math.random() * infoDeafaultData.length)
            ],
          socials: updatedSocials,
          stats: DEFAULT_OWNER_STATE.stats,
        };

        setOwnerData(initialState);
        setOriginalOwnerData(initialState);
      } catch (err) {
        console.error("Error fetching owner data:", err);
        setError(
          err instanceof Error ? err.message : "Failed to fetch owner data"
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchOwnerData();
  }, [address, reloadTrigger]);

  // Check if data has changed
  const hasChanges = () => {
    return (
      ownerData.username !== originalOwnerData.username ||
      ownerData.info !== originalOwnerData.info ||
      ownerData.avatar !== originalOwnerData.avatar ||
      ownerData.background !== originalOwnerData.background ||
      JSON.stringify(ownerData.socials) !==
        JSON.stringify(originalOwnerData.socials) ||
      JSON.stringify(ownerData.stats) !==
        JSON.stringify(originalOwnerData.stats)
    );
  };

  return {
    ownerData,
    setOwnerData,
    originalOwnerData,
    setOriginalOwnerData,
    isLoading,
    error,
    stats,
    hasChanges: hasChanges(),
  };
};
